/**
 * useDrills — React hook for fetching drills from Supabase.
 *
 * Falls back to static mockData if Supabase fetch fails or returns empty,
 * ensuring the platform always shows content even without DB connectivity.
 *
 * Usage:
 *   const { items, loading, error } = useDrills("pd");
 *   const { items, loading } = useDrills("movement");
 */

import { useState, useEffect, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { CONTENT } from "../constants/navigation";
import { TEAM_CATEGORY_IDS, TEAM_PD_CATEGORY_NAMES } from "../constants/categories";
import { PRO_COLLECTION, NOT_HELD, excludeHeld } from "../constants/collections";

// In-memory cache to avoid refetching on every nav
const cache = {};

// Only the columns mapRow() actually uses. select("*") pulled heavy unused
// columns (embeddings, AI blobs, transcripts) which made first-load of big
// sections like X&O slow. Keep this in sync with mapRow below.
const DRILL_COLS = "id, title, category, type, coach_id, level, duration, mux_playback_id, mux_asset_id, description, ai_description, tags, is_new, publish_at, coaching_points, has_narration, ai_enriched_at, xo_category, xo_subcategory, categories, collections, format, section, sort_order, created_at, status, tease, collection_only";

function fmtDuration(seconds) {
  const s = parseInt(seconds) || 0;
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss.toString().padStart(2, "0")}`;
}

// Seconds for comparing, from either shape the column has held: a number
// of seconds, or a legacy "12:34" display string. Anything else is 0, which
// sorts as short rather than throwing a drill onto the wrong shelf.
export function durationSeconds(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return 0;
  const parts = value.trim().split(":");
  if (parts.length < 2 || parts.length > 3) return Number(value) || 0;
  return parts.reduce((acc, p) => {
    const n = Number(p);
    return Number.isFinite(n) ? acc * 60 + n : acc * 60;
  }, 0);
}

export function mapRow(row) {
  // Prefer the manually-entered description. Fall back to the AI-generated
  // one only when the human column is empty, so handcrafted copy always wins.
  const effectiveDesc = (row.description && row.description.trim())
    ? row.description
    : (row.ai_description || "");
  return {
    id: row.id,
    title: row.title || "",
    category: row.category || "General",
    type: row.type || null,
    coachId: row.coach_id || null,
    level: row.level || "All Levels",
    duration: typeof row.duration === "number" ? fmtDuration(row.duration) : (row.duration || "0:00"),
    // Raw seconds alongside the display string. "12:34" is for reading,
    // not for comparing, and the Pro Development shelves split on length.
    durationSec: durationSeconds(row.duration),
    muxPlaybackId: row.mux_playback_id || null,
    muxAssetId: row.mux_asset_id || null,
    desc: effectiveDesc,
    sub: effectiveDesc ? effectiveDesc.slice(0, 80) : "",
    tag: row.category || "General",
    tags: Array.isArray(row.tags) ? row.tags : [],
    isNew: row.is_new || false,
    createdAt: row.created_at || null,
    publishAt: row.publish_at || null,
    // ContentDetail reads `item.keyPoints`; surface coaching_points there.
    keyPoints: Array.isArray(row.coaching_points) ? row.coaching_points : (row.coaching_points ? [row.coaching_points] : []),
    hasNarration: row.has_narration ?? null,
    aiEnriched: !!row.ai_enriched_at,
    // X & O classification (only set when section === 'xo')
    xoCategory:    row.xo_category    || null,
    xoSubcategory: row.xo_subcategory || null,
    // Curated multi-select taxonomy (Team Coaching). See
    // src/dashboard/constants/categories.js for the canonical list.
    categories: Array.isArray(row.categories) ? row.categories : [],
    // "Tease this" — an unpublished drill deliberately shown as a
    // greyed-out card. The card, the Coming Soon tab and the takeover
    // were all built for masterclasses and key off `comingSoon` /
    // `availableAt`, so the flag is translated into that shape here
    // rather than teaching every one of them a third field.
    //
    // A teased drill with a future publish_at is dated ("DROPS Thu 3
    // Oct"); one without is dateless ("COMING SOON"). A publish_at in
    // the past means the cron just hasn't run yet, so it reads as
    // dateless rather than announcing a drop that already happened.
    ...(() => {
      // Guard on status as well as the flag. A published drill is
      // watchable, so greying it out would be a bug however the flag
      // got set; an archived one is hidden on purpose.
      const teasable = row.status === "draft" || row.status === "scheduled";
      if (row.tease !== true || !teasable) return { comingSoon: false, availableAt: null };
      const ts = row.publish_at ? new Date(row.publish_at).getTime() : 0;
      const dated = !!ts && !Number.isNaN(ts) && ts > Date.now();
      return { comingSoon: !dated, availableAt: dated ? row.publish_at : null };
    })(),
    tease: row.tease === true,
    // Hand-curated shelf membership (e.g. "pro"). Never AI-written.
    collections: Array.isArray(row.collections) ? row.collections : [],
    // Only meaningful as a signal that a human placed this row. Almost
    // everything is 0, which is exactly what makes it usable: the daily
    // shuffle pins the placed ones and rotates the rest.
    sortOrder: Number(row.sort_order || 0),
    // Which library the video actually lives in. The collection page
    // groups by this (Full Workouts / Drills / Insights), so it has to
    // survive the mapping even though section pages don't read it.
    section: row.section || null,
    // Binary format axis: "on-air" | "ssg" | null.
    format: row.format || null,
  };
}

export function useDrills(sectionId) {
  const [items, setItems] = useState(() => {
    // Start with cached data if available
    if (cache[sectionId]) return cache[sectionId];
    // Uncached: start EMPTY so ContentPage shows its skeleton during the
    // fetch. Seeding static mock content here painted fake cards for ~200ms,
    // then swapped to live data — a visible flash. The static fallback still
    // applies on fetch failure / empty DB (handled in the fetch below).
    return [];
  });
  const [loading, setLoading] = useState(!cache[sectionId]);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    // If cached and fresh (< 5 min), skip fetch
    if (cache[sectionId] && cache[`${sectionId}_ts`] && Date.now() - cache[`${sectionId}_ts`] < 300000) {
      setItems(cache[sectionId]);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        // Team Coaching cross-pollinates: a PD drill tagged with one of
        // the curated team-tactical categories (transition, scrimmage,
        // half-court-offense, etc) should surface here too. We fetch
        // both sections and filter PD down to the cross-relevant subset
        // client-side; team rows always pass through.
        const isTeam = sectionId === "team";
        // The collection page is not a section. It gathers everything
        // anyone put on the shelf, whatever section the video lives in
        // — full workouts, single drills, insights, masterclasses — so
        // it filters on the array instead of `section`.
        const isCollection = sectionId === "pro";
        let q = supabase
          .from("drills")
          .select(DRILL_COLS)
          // Teased drills have to come past the published-only gate.
          // Scoped to draft and scheduled inside the or(): archived is
          // hidden deliberately, and a stale flag on an archived row
          // should not put it back in front of coaches.
          .or("status.eq.published,status.is.null,and(tease.is.true,status.in.(draft,scheduled))")
          .order("sort_order", { ascending: true, nullsFirst: false })
          .order("created_at", { ascending: false });
        if (isCollection) {
          q = q.contains("collections", [PRO_COLLECTION]);
        } else {
          // Held rows are finished and live, but only on their
          // collection page. They rejoin the section libraries when
          // someone releases them, which is one flag rather than a
          // re-tagging job. `or` rather than `eq(false)` so rows that
          // predate the column (null) are not silently dropped.
          q = excludeHeld(q);
          if (isTeam) q = q.in("section", ["team", "pd"]);
          else        q = q.eq("section", sectionId);
        }
        const { data, error: dbError } = await q;

        if (!mounted.current) return;

        if (dbError) throw dbError;

        if (data && data.length > 0) {
          const filtered = isTeam
            ? data.filter(row => {
                if (row.section === "team") return true;
                // Newer multi-select taxonomy (currently unpopulated).
                const cats = Array.isArray(row.categories) ? row.categories : [];
                if (cats.some(c => TEAM_CATEGORY_IDS.includes(c))) return true;
                // Legacy singular `category` field — how tactical PD drills
                // were originally surfaced on the Team page.
                return TEAM_PD_CATEGORY_NAMES.includes(row.category);
              })
            : data;
          const mapped = filtered.map(mapRow);
          cache[sectionId] = mapped;
          cache[`${sectionId}_ts`] = Date.now();
          setItems(mapped);
        } else {
          // No DB data — use static fallback
          const fallback = CONTENT[sectionId]?.items || [];
          if (fallback.length > 0) {
            setItems(fallback);
          }
        }
      } catch (e) {
        if (!mounted.current) return;
        console.warn(`[useDrills] ${sectionId} fetch failed, using static fallback:`, e.message);
        setError(e.message);
        // Keep existing items (static fallback)
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();

    return () => { mounted.current = false; };
  }, [sectionId]);

  return { items, loading, error };
}

/**
 * One drill by id, for a surface that shows a single drill without loading
 * its whole section: a drill shared into a Community post opens in place.
 * Reads a section already in memory first, so a coach who came from the
 * library pays nothing. Same visibility as the library: published, or teased.
 * null when it isn't there (deleted, archived) or the read fails.
 */
export async function fetchDrillById(id) {
  if (id == null || id === "") return null;
  for (const k of Object.keys(cache)) {
    const list = cache[k];
    if (!Array.isArray(list)) continue;
    const hit = list.find(i => String(i.id) === String(id));
    if (hit) return hit;
  }
  try {
    const { data, error } = await supabase
      .from("drills")
      .select(DRILL_COLS)
      .eq("id", id)
      .or("status.eq.published,status.is.null,and(tease.is.true,status.in.(draft,scheduled))")
      .maybeSingle();
    if (error || !data) return null;
    return mapRow(data);
  } catch {
    return null;
  }
}

/**
 * useAllDrills — fetch all drills across all sections for dashboard/search.
 */
export function useAllDrills() {
  const [items, setItems] = useState(() => cache._all || []);
  const [loading, setLoading] = useState(!cache._all);

  useEffect(() => {
    if (cache._all && cache._all_ts && Date.now() - cache._all_ts < 300000) {
      setItems(cache._all);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from("drills")
          .select(DRILL_COLS)
          // Published only, deliberately. This feeds global search and
          // the dashboard rails, which open an item by their own paths
          // and never consult the pre-release gate. A teased drill
          // surfacing there would play rather than tease, so the
          // greying stays where the gate is: the library grid.
          .or("status.eq.published,status.is.null")
          // Held rows stay out of here too. This feeds global search and
          // the dashboard rails, and a drill being held back from the
          // library is not held back if search hands it over anyway.
          .or(NOT_HELD)
          .order("created_at", { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          const mapped = data.map(row => ({ ...mapRow(row), sectionId: row.section || "pd" }));
          cache._all = mapped;
          cache._all_ts = Date.now();
          setItems(mapped);
        }
      } catch (e) {
        console.warn("[useAllDrills] fetch failed, using static fallback:", e.message);
        // Build from static CONTENT
        const all = Object.entries(CONTENT).flatMap(([sid, sec]) =>
          (sec.items || []).map(i => ({ ...i, sectionId: sid }))
        );
        if (all.length > 0 && items.length === 0) setItems(all);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { items, loading };
}
